import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Play, RefreshCw, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  fetchVoices,
  testSynthesis,
  fetchCloneRequests,
  submitCloneRequest,
  pollCloneRequest,
  type VoiceLibraryVoice,
  type VoiceCloneRequest,
} from '@/lib/voiceLibrary';

// -------------------------------------------------------------------------
// VoiceLibraryPage — Story 68.3 + 68.2
// Route: /admin/voice-library
// -------------------------------------------------------------------------

type Tab = 'voices' | 'clones';

export function VoiceLibraryPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('voices');
  const [voices, setVoices] = useState<VoiceLibraryVoice[]>([]);
  const [clones, setClones] = useState<VoiceCloneRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, c] = await Promise.all([fetchVoices(), fetchCloneRequests()]);
        if (!cancelled) {
          setVoices(v);
          setClones(c);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Mic className="h-6 w-6" />
          {t('voiceLibrary.title')}
        </h1>
        <p className="text-muted-foreground mt-1">{t('voiceLibrary.subtitle')}</p>
      </div>

      <div className="flex gap-4 mb-6 border-b">
        {(['voices', 'clones'] as Tab[]).map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              tab === tabKey
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`voiceLibrary.tab_${tabKey}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          {t('common.loading')}
        </div>
      ) : tab === 'voices' ? (
        <VoicesTab
          voices={voices}
          onVoicesChange={setVoices}
        />
      ) : (
        <ClonesTab
          clones={clones}
          onClonesChange={setClones}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Voices Tab (Story 68.3)
// -------------------------------------------------------------------------

interface VoicesTabProps {
  voices: VoiceLibraryVoice[];
  onVoicesChange: (v: VoiceLibraryVoice[]) => void;
}

function VoicesTab({ voices }: VoicesTabProps) {
  const { t } = useTranslation();
  const [testingVoice, setTestingVoice] = useState<VoiceLibraryVoice | null>(null);

  if (voices.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-12">{t('voiceLibrary.empty_voices')}</p>
    );
  }

  return (
    <>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-muted-foreground text-left">
            <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_name')}</th>
            <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_accent')}</th>
            <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_gender')}</th>
            <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_provider')}</th>
            <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_type')}</th>
            <th className="pb-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {voices.map((v) => (
            <tr key={v.id} className="border-b last:border-0 hover:bg-muted/30">
              <td className="py-2 pr-4 font-medium">{v.name}</td>
              <td className="py-2 pr-4 text-muted-foreground">{v.accent}</td>
              <td className="py-2 pr-4">{t(`voiceLibrary.gender_${v.gender}`)}</td>
              <td className="py-2 pr-4 text-muted-foreground">{v.provider}</td>
              <td className="py-2 pr-4">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    v.isCloned
                      ? 'bg-purple-900/40 text-purple-300'
                      : 'bg-blue-900/40 text-blue-300'
                  }`}
                >
                  {v.isCloned ? t('voiceLibrary.type_cloned') : t('voiceLibrary.type_system')}
                </span>
              </td>
              <td className="py-2 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setTestingVoice(v)}
                >
                  <Play className="h-3.5 w-3.5 mr-1" />
                  {t('voiceLibrary.btn_test')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {testingVoice && (
        <TestSynthesisDialog
          voice={testingVoice}
          onClose={() => setTestingVoice(null)}
        />
      )}
    </>
  );
}

// -------------------------------------------------------------------------
// Test Synthesis Dialog (Story 68.3 "test" button)
// -------------------------------------------------------------------------

interface TestSynthesisDialogProps {
  voice: VoiceLibraryVoice;
  onClose: () => void;
}

function TestSynthesisDialog({ voice, onClose }: TestSynthesisDialogProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(
    'Hola, soy tu agente de Kairos. ¿Cómo le puedo ayudar hoy?',
  );
  const [synthesising, setSynthesising] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  async function handleSynthesize() {
    setSynthesising(true);
    setError(null);
    try {
      const blob = await testSynthesis(voice.id, text);
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        await audioRef.current.play();
      }
    } catch {
      setError(t('voiceLibrary.test_error'));
    } finally {
      setSynthesising(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">
          {t('voiceLibrary.test_dialog_title', { name: voice.name })}
        </h2>
        <label className="block text-sm font-medium mb-1">
          {t('voiceLibrary.test_dialog_text_label')}
        </label>
        <textarea
          className="w-full border rounded px-3 py-2 text-sm resize-none h-20 mb-3"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('voiceLibrary.test_dialog_text_placeholder')}
        />
        {error && <p className="text-destructive text-sm mb-3">{error}</p>}
        <audio ref={audioRef} className="w-full mb-4" controls />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSynthesize} disabled={synthesising || !text.trim()}>
            {synthesising ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                {t('voiceLibrary.test_dialog_synthesising')}
              </>
            ) : (
              t('voiceLibrary.test_dialog_synthesise')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Clones Tab (Story 68.2)
// -------------------------------------------------------------------------

interface ClonesTabProps {
  clones: VoiceCloneRequest[];
  onClonesChange: (c: VoiceCloneRequest[]) => void;
}

function ClonesTab({ clones, onClonesChange }: ClonesTabProps) {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);

  async function handlePoll(requestId: string) {
    const updated = await pollCloneRequest(requestId);
    onClonesChange(clones.map((c) => (c.id === requestId ? updated : c)));
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowDialog(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t('voiceLibrary.btn_clone')}
        </Button>
      </div>

      {clones.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">{t('voiceLibrary.empty_clones')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-muted-foreground text-left">
              <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_name')}</th>
              <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_status')}</th>
              <th className="pb-2 pr-4 font-medium">{t('voiceLibrary.col_requested')}</th>
              <th className="pb-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {clones.map((c) => (
              <tr key={c.id} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium">{c.voiceName}</td>
                <td className="py-2 pr-4">
                  <CloneStatusBadge status={c.status} />
                </td>
                <td className="py-2 pr-4 text-muted-foreground">
                  {new Date(c.requestedAt).toLocaleDateString()}
                </td>
                <td className="py-2 text-right">
                  {(c.status === 'PENDING' || c.status === 'TRAINING') && (
                    <Button variant="ghost" size="sm" onClick={() => handlePoll(c.id)}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" />
                      {t('voiceLibrary.poll_refresh')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showDialog && (
        <CloneDialog
          onClose={() => setShowDialog(false)}
          onCreate={(newClone) => {
            onClonesChange([newClone, ...clones]);
            setShowDialog(false);
          }}
        />
      )}
    </>
  );
}

function CloneStatusBadge({ status }: { status: VoiceCloneRequest['status'] }) {
  const { t } = useTranslation();
  const classes: Record<string, string> = {
    PENDING: 'bg-muted text-foreground',
    TRAINING: 'bg-yellow-900/40 text-yellow-300',
    READY: 'bg-green-900/40 text-green-300',
    FAILED: 'bg-red-900/40 text-red-300',
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${classes[status]}`}>
      {t(`voiceLibrary.status_${status}`)}
    </span>
  );
}

// -------------------------------------------------------------------------
// Clone Dialog (Story 68.2 submit UI)
// -------------------------------------------------------------------------

interface CloneDialogProps {
  onClose: () => void;
  onCreate: (c: VoiceCloneRequest) => void;
}

function CloneDialog({ onClose, onCreate }: CloneDialogProps) {
  const { t } = useTranslation();
  const [voiceName, setVoiceName] = useState('');
  const [sampleUrl, setSampleUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!voiceName.trim() || !sampleUrl.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitCloneRequest(voiceName, sampleUrl);
      onCreate(result);
    } catch {
      setError(t('voiceLibrary.clone_error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">{t('voiceLibrary.clone_dialog_title')}</h2>

        <label className="block text-sm font-medium mb-1">
          {t('voiceLibrary.clone_name_label')}
        </label>
        <input
          className="w-full border rounded px-3 py-2 text-sm mb-3"
          value={voiceName}
          onChange={(e) => setVoiceName(e.target.value)}
          placeholder={t('voiceLibrary.clone_name_placeholder')}
        />

        <label className="block text-sm font-medium mb-1">
          {t('voiceLibrary.clone_url_label')}
        </label>
        <input
          className="w-full border rounded px-3 py-2 text-sm mb-1"
          value={sampleUrl}
          onChange={(e) => setSampleUrl(e.target.value)}
          placeholder="https://storage.example.com/sample.mp3"
        />
        <p className="text-xs text-muted-foreground mb-3">{t('voiceLibrary.clone_url_hint')}</p>

        {error && <p className="text-destructive text-sm mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !voiceName.trim() || !sampleUrl.trim()}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                {t('voiceLibrary.clone_submitting')}
              </>
            ) : (
              t('voiceLibrary.clone_submit')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
